import { useState, useEffect, useCallback } from 'react';
import { getCacheSize, formatCacheSize, clearCache } from '../serviceWorkerRegistration';

type OfflineStatus = {
  isOffline: boolean;
  cacheSize: string;
  cacheSizeBytes: number;
  lastOnline: Date | null;
  refreshCacheSize: () => Promise<void>;
  clearAllCache: () => Promise<boolean>;
};

export function useOfflineStatus(): OfflineStatus {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [cacheSizeBytes, setCacheSizeBytes] = useState(0);
  const [lastOnline, setLastOnline] = useState<Date | null>(
    navigator.onLine ? new Date() : null
  );

  const refreshCacheSize = useCallback(async () => {
    const size = await getCacheSize();
    setCacheSizeBytes(size);
  }, []);

  const clearAllCache = useCallback(async () => {
    const success = await clearCache();
    if (success) {
      setCacheSizeBytes(0);
    }
    return success;
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setLastOnline(new Date());
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial cache size fetch
    refreshCacheSize();

    // Refresh cache size periodically
    const interval = setInterval(refreshCacheSize, 60000); // Every minute

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [refreshCacheSize]);

  return {
    isOffline,
    cacheSize: formatCacheSize(cacheSizeBytes),
    cacheSizeBytes,
    lastOnline,
    refreshCacheSize,
    clearAllCache,
  };
}

export default useOfflineStatus;
